import { Box, Drawer as MuiDrawer } from "@mui/material";
import { CSSObject, styled, Theme } from "@mui/material/styles";
import { useEffect, useState } from "react";

import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  ADMIN_DASHBOARD_TAB_STORAGE_KEY,
  SidebarMenu,
} from "src/constants/common";
import { CustomStyles, getStyles } from "src/styles/theme";
import LogoWrapper from "./LogoWrapper";
import defaultStyles, { StylesClasses } from "./styles";

type SideBarProps = {
  customStyles?: CustomStyles<StylesClasses>;
};

const drawerWidth = 260;

const paperScroll = (theme: Theme): CSSObject => ({
  position: "fixed",
  top: 0,
  height: "100vh",
  overflowY: "auto",
  overflowX: "hidden",
  backgroundColor: theme.palette.custom.background.dark,
  borderRight: "none",
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(255, 255, 255, 0.25) transparent",
  "&::-webkit-scrollbar": { width: "6px" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(255, 255, 255, 0.25)",
    borderRadius: "3px",
  },
});

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(8)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": { ...openedMixin(theme), ...paperScroll(theme) },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": { ...closedMixin(theme), ...paperScroll(theme) },
  }),
}));

const SideBar = ({ customStyles }: SideBarProps) => {
  const [open, setOpen] = useState(true);
  const styles = getStyles<StylesClasses>(defaultStyles, customStyles);
  const location = useLocation();

  const menuPaths = SidebarMenu.map((menuItem) => menuItem.path);
  const isOnMenuPath = menuPaths.includes(location.pathname);
  const [activeMenuPath, setActiveMenuPath] = useState<string>(() =>
    isOnMenuPath
      ? location.pathname
      : localStorage.getItem("activeMenuPath") || "",
  );

  useEffect(() => {
    if (isOnMenuPath) {
      setActiveMenuPath(location.pathname);
      localStorage.setItem("activeMenuPath", location.pathname);
    }
  }, [location.pathname, isOnMenuPath]);

  const highlightedPath = isOnMenuPath ? location.pathname : activeMenuPath;
  const isActive = (itemPath: string) => itemPath === highlightedPath;

  const getNavigationPath = (itemPath: string) => {
    if (itemPath !== "/market-watch") return itemPath;

    const savedTab = localStorage.getItem(ADMIN_DASHBOARD_TAB_STORAGE_KEY);
    return savedTab
      ? `${itemPath}?tab=${encodeURIComponent(savedTab)}`
      : itemPath;
  };

  const handleDrawerOpen = () => {
    setOpen(!open);
  };

  return (
    <Drawer variant="permanent" open={open}>
      <Box {...styles("container")}>
        <Box>
          <LogoWrapper onClick={handleDrawerOpen} open={open} />
        </Box>
        <Box>
          <Box {...styles("divider")}></Box>

          {SidebarMenu.map((item) => {
            return (
              <Box
                component={RouterLink}
                to={getNavigationPath(item.path)}
                key={item.text}
                {...styles("navItemsContainer", {
                  ...(isActive(item.path) &&
                    defaultStyles.activeNavContainer),
                  paddingLeft: open ? "16px" : "8px",
                })}
              >
                <Box component="img" src={item.icon} />
                <Box
                  {...styles("navText", {
                    ...(isActive(item.path) &&
                      defaultStyles.activeNavText),
                    display: open ? "block" : "none",
                  })}
                >
                  {item.text}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Drawer>
  );
};

export default SideBar;
